/**
 * Contracts List Container
 *
 * Handles rendering contracts in table view with loading and error states
 */

import type { ContractResponse } from '@monorepo/shared/schemas/contracts'
import type { ContractFilters } from '../hooks/useContracts'
import { ContractsTable } from './ContractsTable'

interface ContractsListProps {
  contracts: ContractResponse[]
  isLoading: boolean
  error: Error | null
  viewMode: 'grid' | 'table'
  filters: ContractFilters
  onSort: (sortBy: string, sortOrder: 'asc' | 'desc') => void
  onPageChange: (page: number) => void
  onContractClick: (contract: ContractResponse) => void
  meta?: {
    total: number
    page: number
    limit: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
}

export function ContractsList({
  contracts,
  isLoading,
  error,
  filters,
  onSort,
  onPageChange,
  onContractClick,
  meta,
}: ContractsListProps) {
  // Error state
  if (error) {
    return (
      <div className='flex items-center justify-center p-12 border border-red-200 bg-red-50 rounded-lg'>
        <div className='text-center'>
          <p className='text-red-600 font-medium'>Failed to load contracts</p>
          <p className='text-sm text-red-500 mt-1'>{error.message}</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='animate-pulse space-y-3'>
          {[...Array(5)].map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              key={`skeleton-${i}`}
              className='h-16 bg-gray-100 rounded-lg'
            />
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (contracts.length === 0) {
    return (
      <div className='flex items-center justify-center p-12 border border-dashed border-gray-300 rounded-lg'>
        <div className='text-center'>
          <p className='text-gray-600 font-medium'>No contracts found</p>
          <p className='text-sm text-gray-500 mt-1'>
            {filters.search || filters.status !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first contract to get started'}
          </p>
        </div>
      </div>
    )
  }

  // Render table view
  return (
    <ContractsTable
      contracts={contracts}
      onSort={onSort}
      onPageChange={onPageChange}
      onContractClick={onContractClick}
      meta={meta}
      filters={filters}
    />
  )
}
